import React from 'react';
import { Link } from 'react-router-dom';
import { Eye, Bell, Search, Server, Lock, Users } from 'lucide-react';
import { useLanguage } from '../components/LanguageContext';
import { translations } from '../translations';

export function Home() {
  const { language } = useLanguage();
  const t = translations.home;

  const features = [
    {
      icon: <Eye className="w-8 h-8 text-blue-500" />,
      title: t.features.monitoring.title[language],
      path: "/monitoring",
      description: t.features.monitoring.description[language]
    },
    {
      icon: <Bell className="w-8 h-8 text-blue-500" />,
      title: t.features.incidentResponse.title[language],
      path: "/incident-response",
      description: t.features.incidentResponse.description[language]
    },
    {
      icon: <Search className="w-8 h-8 text-blue-500" />,
      title: t.features.threatHunting.title[language],
      path: "/threat-hunting",
      description: t.features.threatHunting.description[language]
    },
    {
      icon: <Server className="w-8 h-8 text-blue-500" />,
      title: t.features.siem.title[language],
      path: "/siem",
      description: t.features.siem.description[language]
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <header className="hero-pattern">
        <div className="container mx-auto px-6 py-16 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-blue-600">
              {t.hero.title[language]}
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              {t.hero.subtitle[language]}
            </p>

            {/* Company Description */}
            <div className="max-w-3xl mx-auto mb-12 space-y-6">
              <p className="text-lg text-gray-300 leading-relaxed">
                {language === 'fr' 
                  ? "Au cœur de Genève, secusoc.io est un leader innovant dans la fourniture de Security Operations Center as a Service (SOCaaS). Notre mission est de traquer sans relâche les menaces cybernétiques qui pèsent sur votre entreprise, en combinant expertise humaine et technologie de pointe pour une protection 24/7."
                  : "In the heart of Geneva, secusoc.io is an innovative leader in providing Security Operations Center as a Service (SOCaaS). Our mission is to relentlessly hunt down cyber threats targeting your business, combining human expertise and cutting-edge technology for 24/7 protection."}
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                {language === 'fr'
                  ? "Motivés par la passion de la cybersécurité, nos experts chassent activement les menaces émergentes, analysent les comportements suspects et neutralisent les attaques avant qu'elles ne compromettent vos systèmes. Notre approche proactive et notre engagement envers l'excellence font de nous le partenaire de confiance des entreprises qui ne font aucun compromis sur leur sécurité."
                  : "Driven by our passion for cybersecurity, our experts actively hunt emerging threats, analyze suspicious behaviors, and neutralize attacks before they compromise your systems. Our proactive approach and commitment to excellence make us the trusted partner for businesses that make no compromises on their security."}
              </p>
            </div>

            <div className="flex flex-col md:flex-row justify-center gap-4">
              <Link 
                to="/get-started"
                className="bg-blue-500 px-8 py-3 rounded-lg text-lg hover:bg-blue-600 transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-blue-500/25"
              >
                {t.hero.cta.start[language]}
              </Link>
              <Link 
                to="/get-started"
                className="border border-white px-8 py-3 rounded-lg text-lg hover:bg-white hover:text-gray-900 transition-all duration-300 transform hover:scale-105"
              >
                {t.hero.cta.demo[language]}
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Features Grid */}
      <section id="features" className="features-pattern py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            {t.features.title[language]}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Link 
                to={feature.path}
                key={index} 
                className="bg-gray-800/50 backdrop-blur-sm p-6 rounded-lg hover:bg-gray-700/50 transition-all duration-300 transform hover:scale-105 border border-gray-700/50"
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-6 py-20">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1544197150-b99a580bb7a8?auto=format&fit=crop&q=80')] opacity-10 bg-cover bg-center"></div>
          <div className="relative z-10">
            <h2 className="text-3xl font-bold text-white mb-4">
              {t.cta.title[language]}
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              {t.cta.subtitle[language]}
            </p>
            <Link 
              to="/get-started"
              className="bg-white text-blue-600 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
            >
              {t.cta.button[language]}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}